import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useParams } from 'react-router-dom';
import { Tabs, Tab, Form, Button, Alert } from 'react-bootstrap';
import MaskedDateTimeField from 'components/forms/MaskedDateTimeField';
import { EditBoolean } from 'components/forms/EditBoolean';
import { DEFAULT_SCHEDULE_NEXT, type ScheduleNext } from 'api/recurrence_definitions/endpoints/types';
import { ScheduleNextFields } from 'pages/apps/platform/recurrence_definitions/edit/ScheduleNextFields';
import moment from 'moment-timezone';
import { fetchCustomerNodeDefinitionByZoneAndName } from '../../../../redux/slices/customer_node_definitionsSlice';
import { useTranslation } from 'react-i18next';
import api from 'services/api';
import { deriveRematerializeWarning } from './deriveRematerializeWarning';
import { UpdateRematerializeAlert } from './UpdateRematerializeAlert';
import { getQueuesPaginated } from '../../../../redux/slices/lists/queuesSlice';
import { RootState, useAppSelector, useAppDispatch } from 'redux/store';
import { fetchSchedulingWindows } from '../../../../redux/slices/scheduling_windowsSlice';
import { canUpdate } from 'utils/constants';
import { LoadingSelect } from 'components/forms/LoadingFields';

const INITIAL_UPDATE_BODY = {
  name: '',
  queue_path: '',
  scheduled_start_datetime: moment().format(),
  scheduled_start_timezone: moment.tz.guess(),
  parameters: [] as Array<{ name: string; value: any }>,
  scheduling_window: '',
  hold_on_submit: false,
  schedule: {
    interval: null as number | null,
    unit: null as 'year' | 'month' | 'day' | 'hour' | 'minute' | null,
    resubmit_on_error: true,
    schedule_next: DEFAULT_SCHEDULE_NEXT as ScheduleNext,
  },
  start_node_id: null as string | null,
};

/**
 * Whether a schedule body actually describes a recurrence, in either of the two
 * shapes the backend stores: the legacy flat `{ interval, unit }` or the
 * `{ recurrences: [...] }` snapshot the submit screen writes.
 *
 * `schedule_next` and `resubmit_on_error` only mean something alongside one of
 * those. NOTE: sending any non-null `schedule` is enough to make an instance
 * schedule-driven, so an update to an instance describing neither shape must
 * omit `schedule` entirely rather than send one carrying only those two.
 */
const describesRecurrence = (schedule: any): boolean =>
  Array.isArray(schedule?.recurrences) ? schedule.recurrences.length > 0 : !!schedule?.interval && !!schedule?.unit;

const normalizeKeyValuePairs = (val: unknown) => {
  if (typeof val !== 'string') return val as any;
  const i = val.indexOf('=');
  return (i >= 0 ? val.slice(0, i) : val).trim();
};

const getDropdownOptions = (optionsStr: string) =>
  (optionsStr || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

const parseKeyValueOptions = (s: string) =>
  (s || '')
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean)
    .map((pair) => {
      const [key, ...rest] = pair.split('=');
      const label = rest.join('=').trim();
      return { key: (key ?? '').trim(), label };
    })
    .filter(({ key, label }) => key && label);

const UpdateNodeSubmit: React.FC = () => {
  const { zone_name, name, instance_id, node_definition_id } = useParams<{
    zone_name: string;
    name: string;
    instance_id: string;
    node_definition_id: string;
  }>();

  useEffect(() => {
    const prev = document.title;
    const base = instance_id ? `Update Instance - ${instance_id} - ${name}` : `Update Definition - ${node_definition_id} - ${name}`;
    document.title = base;
    return () => {
      document.title = prev;
    };
  }, [instance_id, node_definition_id, name]);

  const [selectedTab, setSelectedTab] = useState('submit');
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  // TODO #757 step 3 PR D: extend CustomerNodeDefinition in the slice with
  // child_nodes, parameters, default_queue_path so this cast goes away.
  const nodeDefinition = useAppSelector((state) =>
    state.customerNodeDefinitions.nodes.find((node: any) => node.zone_name === zone_name && node.name === name)
  ) as any;

  const queuesState = useAppSelector((state) => state.listOfQueues);
  const queues = queuesState?.queues || [];
  const isQueuesLoading = queuesState.loading?.getQueuesPaginated || false;

  const schedulingWindowState = useAppSelector((state) => (state as any).schedulingWindow || {});
  const schedulingWindows = schedulingWindowState.items || [];
  const isSchedulingWindowsLoading = schedulingWindowState.loading?.fetchSchedulingWindows || schedulingWindowState.loading?.getSchedulingWindows || false;

  const customerNodeDefinitionsLoading = useAppSelector((state) => (state as any).customerNodeDefinitions?.loading || {});
  const isNodeDefinitionLoading = customerNodeDefinitionsLoading.fetchCustomerNodeDefinitionByZoneAndName || false;

  const [updateBody, setUpdateBody] = useState(INITIAL_UPDATE_BODY);
  const [initialBody, setInitialBody] = useState(INITIAL_UPDATE_BODY);

  const [showAlert, setShowAlert] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [rematerializeWarning, setRematerializeWarning] = useState<string | null>(null);
  const [datetimeValid, setDatetimeValid] = useState(true);
  const [availablePeriods, setAvailablePeriods] = useState<{ open: string; close: string }[]>([]);
  const [isWindowLoading, setIsWindowLoading] = useState(false);

  const [instanceStatus, setInstanceStatus] = useState<string | null>(null);
  const appliedHoldDefaultRef = useRef<string | null>(null);

  const [paramsDirty, setParamsDirty] = useState(false);

  const startNodeOptions = useMemo(() => {
    if (!Array.isArray(nodeDefinition?.child_nodes)) return [];
    return nodeDefinition.child_nodes.map((n: any) => ({
      label: `${n.node_definition_name || n.name || n.data?.label || 'Unknown'} (${n.type}) — ${n.id}`,
      value: n.id,
    }));
  }, [nodeDefinition]);

  useEffect(() => {
    if (zone_name && name) {
      dispatch(
        fetchCustomerNodeDefinitionByZoneAndName({
          zone_name,
          node_definition_name: name,
        })
      );
    }
  }, [dispatch, zone_name, name]);

  const getNodeInstanceDetails = async () => {
    return await api.get(`/node_instance_details/${instance_id}`);
  };

  const getNodeInstanceById = async () => {
    return await api.get(`/node_instances/${instance_id}`);
  };

  // Prefill form with current instance data AND capture initialBody
  useEffect(() => {
    if (instance_id) {
      const fetchData = async () => {
        try {
          const response = await getNodeInstanceDetails();
          const fullResponse = await getNodeInstanceById();

          if (fullResponse.data?.status) {
            setInstanceStatus(fullResponse.data.status);
          }

          const next = { ...INITIAL_UPDATE_BODY };

          if (response.data?.node_instance?.parameters) {
            next.parameters = response.data.node_instance.parameters;
          }

          if (response.data?.node_instance?.queue_path) {
            next.queue_path = response.data.node_instance.queue_path;
          }

          if (fullResponse.data?.scheduling_window) {
            next.scheduling_window = fullResponse.data.scheduling_window;
          }

          if (fullResponse.data?.schedule) {
            next.schedule = fullResponse.data.schedule;
          }

          if (fullResponse.data?.scheduled_start_datetime) {
            next.scheduled_start_datetime = fullResponse.data.scheduled_start_datetime;
          }

          if (fullResponse.data?.scheduled_start_timezone) {
            next.scheduled_start_timezone = fullResponse.data.scheduled_start_timezone;
          }

          if (fullResponse.data?.start_node_id !== undefined) {
            next.start_node_id = fullResponse.data.start_node_id;
          }

          if (fullResponse.data?.hold_on_submit !== undefined) {
            next.hold_on_submit = !!fullResponse.data.hold_on_submit;
          }

          setUpdateBody(next);
          setInitialBody(next);
          setParamsDirty(false);
        } catch (error) {
          console.error('Error fetching node instance details:', error);
        }
      };
      fetchData();
    }
  }, [instance_id]);

  useEffect(() => {
    dispatch(getQueuesPaginated({ page: 1, limit: 2000 }));
    dispatch(fetchSchedulingWindows({}));
  }, [dispatch]);

  useEffect(() => {
    if (!nodeDefinition || instance_id) return;
    const holdDefault = (nodeDefinition as any).hold_on_submit;
    if (holdDefault === undefined) return;

    // Apply definition default once per definition; don't overwrite later user toggles.
    const definitionKey = String(nodeDefinition.node_definition_id ?? '');
    if (appliedHoldDefaultRef.current === definitionKey) return;
    const holdOnSubmitDefault = Boolean(holdDefault);

    setUpdateBody((prev) => ({
      ...prev,
      hold_on_submit: holdOnSubmitDefault,
    }));
    setInitialBody((prev) => ({
      ...prev,
      hold_on_submit: holdOnSubmitDefault,
    }));
    appliedHoldDefaultRef.current = definitionKey;
  }, [nodeDefinition, instance_id]);

  const handleTabChange = (key: string | null) => {
    if (key) setSelectedTab(key);
  };

  const isUpdatable = instanceStatus ? canUpdate.includes(instanceStatus) : false;

  const pickerValue = useMemo(() => {
    return moment(updateBody.scheduled_start_datetime).tz(updateBody.scheduled_start_timezone || moment.tz.guess());
  }, [updateBody.scheduled_start_datetime, updateBody.scheduled_start_timezone]);

  const handleDatetimeChange = (datetime: moment.Moment | string) => {
    if (typeof datetime === 'string') return;

    const timezone = updateBody.scheduled_start_timezone || moment.tz.guess();

    const current = moment(updateBody.scheduled_start_datetime).tz(timezone);

    const dateChanged = !datetime.isSame(current, 'day');
    const timeChanged = datetime.hour() !== current.hour() || datetime.minute() !== current.minute();

    const zoned = dateChanged || timeChanged ? datetime.clone().seconds(0).milliseconds(0).tz(timezone) : datetime.clone().tz(timezone);

    setUpdateBody((prev) => ({
      ...prev,
      scheduled_start_datetime: zoned.format(),
    }));

    // If user picked a date beyond known periods, refetch with this timestamp
    if (updateBody.scheduling_window && availablePeriods.length > 0) {
      const selectedLocal = zoned.clone();

      // Find last close in LOCAL time
      let lastCloseLocal: moment.Moment | null = null;
      availablePeriods.forEach((p) => {
        const closeLocal = moment.utc(p.close).tz(timezone);
        if (!lastCloseLocal || closeLocal.isAfter(lastCloseLocal)) {
          lastCloseLocal = closeLocal;
        }
      });

      if (lastCloseLocal && selectedLocal.isAfter(lastCloseLocal, 'day')) {
        const [zone, winName] = updateBody.scheduling_window.split('|');
        const win = schedulingWindows.find((w) => w.zone_name === zone && w.name === winName);

        if (win) {
          setIsWindowLoading(true);
          api
            .post('/validate-scheduling-window', {
              scheduling_window_id: win.scheduling_window_id,
              timestamp: selectedLocal.toISOString(),
              timezone: updateBody.scheduled_start_timezone,
            })
            .then((res) => {
              const periods = (res.data.available_periods || []).map((p: any) => ({
                open: moment.utc(p.open).toISOString(),
                close: moment.utc(p.close).toISOString(),
              }));
              setAvailablePeriods(periods);
            })
            .catch((err) => {
              console.error('❌ Error reloading available periods:', err);
              setAvailablePeriods([]);
            })
            .finally(() => {
              setIsWindowLoading(false);
            });
        }
      }
    }
  };

  const refetchSchedulingWindowForTimezone = async (timezone: string) => {
    if (!updateBody.scheduling_window) return;

    const [zone, winName] = updateBody.scheduling_window.split('|');
    const win = schedulingWindows.find((w) => w.zone_name === zone && w.name === winName);
    if (!win) return;

    setIsWindowLoading(true);
    try {
      const ts = moment(updateBody.scheduled_start_datetime).tz(timezone).toISOString();

      const res = await api.post('/validate-scheduling-window', {
        scheduling_window_id: win.scheduling_window_id,
        timestamp: ts,
        timezone,
      });

      const periods = (res.data.available_periods || []).map((p: any) => ({
        open: moment.utc(p.open).toISOString(),
        close: moment.utc(p.close).toISOString(),
      }));

      setAvailablePeriods(periods);
    } catch (err) {
      console.error('❌ Error reloading available periods on timezone change:', err);
      setAvailablePeriods([]);
    } finally {
      setIsWindowLoading(false);
    }
  };

  const handleTimezoneChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement> = (e) => {
    const timezone = e.currentTarget.value;
    const asMoment = moment(updateBody.scheduled_start_datetime).tz(timezone);
    setUpdateBody((prev) => ({
      ...prev,
      scheduled_start_timezone: timezone,
      scheduled_start_datetime: asMoment.format(),
    }));

    if (updateBody.scheduling_window) {
      refetchSchedulingWindowForTimezone(timezone);
    }
  };

  const handleQueueChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement> = (e) => {
    const queue_path = e.currentTarget.value;
    setUpdateBody((prev) => ({ ...prev, queue_path }));
  };

  const handleScheduleChange = (e: React.ChangeEvent<any>) => {
    const target = e.target as HTMLInputElement | HTMLSelectElement;
    const { name, value, type } = target;

    const finalValue = type === 'checkbox' && target instanceof HTMLInputElement ? target.checked : name === 'interval' ? Number(value) : value;

    setUpdateBody((prev) => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        [name]: finalValue as any,
      },
    }));
  };

  const handleSchedulingWindowChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement> = async (e) => {
    const selectedValue = e.currentTarget.value;
    setUpdateBody((prev) => ({ ...prev, scheduling_window: selectedValue }));

    if (!selectedValue) {
      setAvailablePeriods([]);
      return;
    }

    setIsWindowLoading(true);

    const [zone, winName] = selectedValue.split('|');
    const win = schedulingWindows.find((w) => w.zone_name === zone && w.name === winName);
    if (!win) {
      setIsWindowLoading(false);
      return;
    }

    try {
      const res = await api.post('/validate-scheduling-window', {
        scheduling_window_id: win.scheduling_window_id,
        timestamp: moment().toISOString(),
        timezone: updateBody.scheduled_start_timezone,
      });

      const periods = (res.data.available_periods || []).map((p: any) => ({
        open: moment.utc(p.open).toISOString(),
        close: moment.utc(p.close).toISOString(),
      }));

      setAvailablePeriods(periods);

      const now = moment().utc();
      const isNowValid = periods.some((p) => now.isBetween(moment(p.open).utc(), moment(p.close).utc(), null, '[]'));

      const defaultStart = isNowValid
        ? moment().tz(updateBody.scheduled_start_timezone)
        : periods.length > 0
          ? moment(periods[0].open).tz(updateBody.scheduled_start_timezone)
          : moment();

      setUpdateBody((prev) => ({
        ...prev,
        scheduled_start_datetime: defaultStart.format(),
      }));
    } catch (err) {
      console.error('❌ Error loading available periods:', err);
      setAvailablePeriods([]);
    } finally {
      setIsWindowLoading(false);
    }
  };

  const handleParameterChange = (paramName: string, parameter_type: string, value: string) => {
    setParamsDirty(true);

    setUpdateBody((prev) => {
      const newParameters = [...prev.parameters];
      const normalizedType = String(parameter_type || '')
        .replace(/\s+/g, '')
        .toLowerCase();
      const finalValue = normalizedType === 'keyvaluedropdown' ? normalizeKeyValuePairs(value) : value;

      const existingParamIndex = newParameters.findIndex((param) => param.name === paramName);

      if (existingParamIndex !== -1) {
        newParameters[existingParamIndex].value = finalValue;
      } else {
        newParameters.push({ name: paramName, value: finalValue });
      }

      return { ...prev, parameters: newParameters };
    });
  };

  const handleStartNodeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    setUpdateBody((prev) => ({
      ...prev,
      start_node_id: val === 'full' ? null : val,
    }));
  };

  const submitInstanceUpdate = async (body: Record<string, unknown>) => {
    setIsSubmitting(true);
    setErrorMessage('');
    setRematerializeWarning(null);
    try {
      if (!instance_id) throw new Error('Missing instance_id for update.');
      const res = await api.put(`/node_instances/${instance_id}`, body);
      setRematerializeWarning(deriveRematerializeWarning(res?.data));
      setShowAlert(true);
    } catch (error: any) {
      console.error('Error updating node instance:', error);
      const msg = error?.response?.data?.error || error?.message || 'Update failed.';
      setErrorMessage(msg);
    } finally {
      setTimeout(() => setIsSubmitting(false), 3000);
    }
  };

  const handleUpdateAll = async () => {
    const { _initial_body, ...restSchedule } = (updateBody.schedule || {}) as any;
    const body: any = {
      ...updateBody,
      schedule: describesRecurrence(restSchedule) ? restSchedule : undefined,
    };

    if (!paramsDirty) delete body.parameters;

    await submitInstanceUpdate(body);
  };

  const isDateValid = (current: moment.Moment) => {
    if (updateBody.scheduling_window === '' || availablePeriods.length === 0) return true;

    const timezone = updateBody.scheduled_start_timezone || moment.tz.guess();

    const currentLocal = current.clone().tz(timezone);
    const currentDateStr = currentLocal.format('YYYY-MM-DD');

    // Find the last close in LOCAL time
    let lastCloseLocal: moment.Moment | null = null;
    availablePeriods.forEach((p) => {
      const closeLocal = moment.utc(p.close).tz(timezone);
      if (!lastCloseLocal || closeLocal.isAfter(lastCloseLocal)) {
        lastCloseLocal = closeLocal;
      }
    });

    // After the last known close day → everything is allowed (so we can refetch)
    if (lastCloseLocal && currentLocal.isAfter(lastCloseLocal, 'day')) {
      return true;
    }

    // Up to and including lastCloseLocal → only OPEN dates are selectable
    return availablePeriods.some((p) => {
      const openLocal = moment.utc(p.open).tz(timezone);
      const openDateStr = openLocal.format('YYYY-MM-DD');
      return currentDateStr === openDateStr;
    });
  };

  const getParamValue = (paramName: string) => updateBody.parameters.find((p: any) => p.name === paramName)?.value ?? '';

  const mergedParams = useMemo(() => {
    const defParams = Array.isArray(nodeDefinition?.parameters) ? nodeDefinition.parameters : [];

    const instByName = new Map<string, any>();
    (updateBody.parameters || []).forEach((p: any) => {
      if (!p || typeof p.name !== 'string') return;
      instByName.set(p.name, p);
    });

    const seen = new Set<string>();
    const out: any[] = [];

    defParams.forEach((p: any) => {
      if (!p || typeof p.name !== 'string') return;
      seen.add(p.name);

      const inst = instByName.get(p.name);
      out.push({
        ...p,
        __instance_value: inst ? inst.value : undefined,
      });
    });

    (updateBody.parameters || []).forEach((p: any) => {
      if (!p || typeof p.name !== 'string') return;
      if (seen.has(p.name)) return;
      out.push({
        name: p.name,
        parameter_type: p.parameter_type || 'string',
        default_expression: p.default_expression || '',
        options: p.options || '',
        __instance_value: p.value,
      });
    });

    return out;
  }, [nodeDefinition, updateBody.parameters]);

  if (!nodeDefinition) return <div>Loading...</div>;

  return (
    <div
      style={{
        flex: '1 1 auto',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
      }}
    >
      {showAlert && !errorMessage && (
        <Alert variant="outline-success" onClose={() => setShowAlert(false)} dismissible>
          Successfully updated.{' '}
          {instance_id && (
            <a href={`/automation/node_instances?node_instance_id=${instance_id}`} target="_parent" rel="noreferrer">
              View Node Instance
            </a>
          )}
        </Alert>
      )}
      {rematerializeWarning && <UpdateRematerializeAlert warning={rematerializeWarning} onDismiss={() => setRematerializeWarning(null)} />}
      {errorMessage && (
        <Alert variant="danger" onClose={() => setErrorMessage('')} dismissible>
          {errorMessage}
        </Alert>
      )}
      {!datetimeValid && (
        <Alert variant="warning" onClose={() => setDatetimeValid(true)} dismissible>
          The selected datetime was outside the scheduling window and has been adjusted to the next valid open period.
        </Alert>
      )}

      <h3>Update Node Instance: {name}</h3>

      <Tabs id="node-update-tabs" activeKey={selectedTab} onSelect={handleTabChange} className="mb-2 border-bottom" variant="underline">
        <Tab eventKey="submit" title="Update">
          <div>
            <Form.Group controlId="scheduled_start_datetime">
              <Form.Label>Scheduled Start Datetime</Form.Label>
              <MaskedDateTimeField
                key={updateBody.scheduling_window}
                value={pickerValue}
                onChange={handleDatetimeChange}
                mode="datetime"
                placeholder="Type or select date and time"
                isValidDate={updateBody.scheduling_window && availablePeriods.length > 0 ? isDateValid : undefined}
              />
            </Form.Group>

            <Form.Group controlId="scheduled_start_timezone">
              <Form.Label>Scheduled Start Timezone</Form.Label>
              <Form.Control as="select" value={updateBody.scheduled_start_timezone} onChange={handleTimezoneChange}>
                {moment.tz.names().map((timezone) => (
                  <option key={timezone} value={timezone}>
                    {timezone}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>

            <LoadingSelect
              label="Queue"
              controlId="queue_name"
              name="queue_path"
              value={updateBody.queue_path ?? ''}
              onChange={handleQueueChange as any}
              isLoading={isQueuesLoading}
              options={[
                ...(instance_id
                  ? [
                      {
                        value: updateBody.queue_path ?? '',
                        label: updateBody.queue_path ?? '',
                      },
                    ]
                  : [
                      {
                        value: '',
                        label: `Default (${nodeDefinition.default_queue_path})`,
                      },
                    ]),
                ...queues.map((queue: any) => ({
                  value: queue.zone_name + '|' + queue.queue_name,
                  label: `${queue.queue_name} (${queue.zone_name})`,
                })),
              ]}
              placeholder=""
              loadingLabel="Loading queues..."
            />

            <Form.Group controlId="schedule" className="mb-3">
              <Form.Label>Schedule</Form.Label>
              <div className="d-flex align-items-center gap-2 mb-2">
                <span>Every </span>
                <Form.Control
                  type="number"
                  name="interval"
                  value={updateBody?.schedule?.interval ?? ''}
                  onChange={handleScheduleChange}
                  min="0"
                  placeholder="Enter number"
                  style={{ maxWidth: '150px' }}
                />
                <Form.Select value={updateBody?.schedule?.unit ?? ''} onChange={handleScheduleChange} name="unit" style={{ maxWidth: '150px' }}>
                  <option value=""></option>
                  <option value="year">Year</option>
                  <option value="month">Month</option>
                  <option value="day">Day</option>
                  <option value="hour">Hour</option>
                  <option value="minute">Minute</option>
                </Form.Select>
              </div>
              {/* Both settings only apply to a recurrence, and an instance without
                  one is never sent a schedule at all. Disabled rather than hidden
                  so the InfoTooltip stays hoverable while greyed out. */}
              <fieldset disabled={!describesRecurrence(updateBody?.schedule)}>
                <ScheduleNextFields
                  scheduleNext={(updateBody?.schedule?.schedule_next ?? DEFAULT_SCHEDULE_NEXT) as ScheduleNext}
                  resubmitOnError={!!updateBody?.schedule?.resubmit_on_error}
                  onScheduleNextChange={(v) =>
                    setUpdateBody((prev) => ({
                      ...prev,
                      schedule: {
                        ...prev.schedule,
                        schedule_next: v,
                      },
                    }))
                  }
                  onResubmitOnErrorChange={(v) => setUpdateBody((prev) => ({ ...prev, schedule: { ...prev.schedule, resubmit_on_error: v } }))}
                />
              </fieldset>
              <EditBoolean
                label="Hold on submit"
                value={!!updateBody?.hold_on_submit}
                onChange={(v) => setUpdateBody((prev) => ({ ...prev, hold_on_submit: v }))}
              />
            </Form.Group>

            <LoadingSelect
              label="Scheduling Window"
              controlId="schedulingWindow"
              name="scheduling_window"
              value={updateBody.scheduling_window || ''}
              onChange={handleSchedulingWindowChange as any}
              isLoading={isSchedulingWindowsLoading || isWindowLoading}
              options={schedulingWindows.map((win: any) => ({
                value: `${win.zone_name}|${win.name}`,
                label: `${win.name} (${win.zone_name})`,
              }))}
              placeholder="Select Scheduling Window"
              loadingLabel={isWindowLoading ? 'Checking scheduling window...' : 'Loading scheduling windows...'}
              disabled={Number.isInteger(nodeDefinition?.restrict_to_scheduling_window)}
            />

            {isWindowLoading && <div className="text-muted small mt-1">Checking scheduling window...</div>}

            {Array.isArray(nodeDefinition.child_nodes) && nodeDefinition.child_nodes.length > 0 && (
              <Form.Group className="mb-3" controlId="start_node_id">
                <Form.Label className="mb-1">Start from</Form.Label>
                <Form.Select
                  value={updateBody.start_node_id ?? 'full'}
                  onChange={handleStartNodeChange}
                  onMouseDown={(e) => {
                    if (isNodeDefinitionLoading) e.preventDefault();
                  }}
                  onKeyDown={(e) => {
                    if (isNodeDefinitionLoading) e.preventDefault();
                  }}
                  aria-busy={isNodeDefinitionLoading || undefined}
                >
                  <option value="full">Run entire flow</option>
                  {startNodeOptions.map((n) => (
                    <option key={n.value} value={n.value}>
                      {n.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}

            <div className="d-flex gap-2 mt-3">
              <Button
                variant="secondary"
                disabled={!isUpdatable || (!nodeDefinition.default_queue_path && updateBody.queue_path === '') || isSubmitting || isWindowLoading}
                onClick={handleUpdateAll}
              >
                Update All
              </Button>
            </div>

            {!isUpdatable && (
              <p style={{ color: 'red', marginBottom: '8px' }}>
                This instance can&apos;t be updated.
                {instanceStatus ? <p> Instance is in a {instanceStatus} state. </p> : <p> Instance status is not avaliable </p>}
              </p>
            )}
          </div>
        </Tab>

        <Tab eventKey="parameters" title="Parameters">
          <div>
            {mergedParams.map((param: any) => {
              const normalizedType = String(param.parameter_type ?? '')
                .replace(/\s+/g, '')
                .toLowerCase();
              const isDropdown = normalizedType === 'dropdown';
              const isKeyValueDropdown = normalizedType === 'keyvaluedropdown';

              const dropdownOptions = isDropdown ? getDropdownOptions(param.options || '') : [];

              const kvOptions = isKeyValueDropdown ? parseKeyValueOptions(param.options || '') : [];

              const instValue = getParamValue(param.name) !== '' ? getParamValue(param.name) : undefined;

              const rawCurrent =
                instValue !== undefined && instValue !== null ? instValue : isDropdown || isKeyValueDropdown ? (param.default_expression ?? '') : '';

              const currentValue = isKeyValueDropdown ? normalizeKeyValuePairs(String(rawCurrent)) : rawCurrent;

              return (
                <Form.Group key={param.name} controlId={`param-${param.name}`} className="mb-3">
                  <Form.Label>{param.name}</Form.Label>

                  {isDropdown && (
                    <Form.Select
                      value={dropdownOptions.includes(String(currentValue)) ? String(currentValue) : ''}
                      onChange={(e) => handleParameterChange(param.name, param.parameter_type, e.target.value)}
                    >
                      {dropdownOptions.length === 0 ? (
                        <option value="">No options configured</option>
                      ) : (
                        dropdownOptions.map((opt: string) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))
                      )}
                    </Form.Select>
                  )}

                  {isKeyValueDropdown && (
                    <Form.Select
                      value={kvOptions.some((o) => o.key === String(currentValue)) ? String(currentValue) : ''}
                      onChange={(e) => handleParameterChange(param.name, param.parameter_type, e.target.value)}
                    >
                      {kvOptions.length === 0 ? (
                        <option value="">No options configured</option>
                      ) : (
                        kvOptions.map(({ key, label }) => (
                          <option key={key} value={key}>
                            {label} ({key})
                          </option>
                        ))
                      )}
                    </Form.Select>
                  )}

                  {!isDropdown && !isKeyValueDropdown && (
                    <Form.Control
                      type="text"
                      placeholder={param.default_expression}
                      value={String(currentValue)}
                      onChange={(e) => handleParameterChange(param.name, param.parameter_type, e.target.value)}
                    />
                  )}
                </Form.Group>
              );
            })}
          </div>
        </Tab>
      </Tabs>
    </div>
  );
};

export default UpdateNodeSubmit;
